import React, {useState, useCallback, useEffect, useMemo, useContext} from 'react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {ListGroup, ListGroupItem, Checkbox, Label, Text, FindAndApply} from '@albert-io/atomic';
import {List, Set} from 'immutable';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';
import sessionStore from 'client/Session/SessionStore';
import {getClassroomSubjectsQuery, excludedIds, getSubjectsByIdQuery} from '../TopicsModal.queries';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import './subjects-filter.scss';

interface SubjectCounts {
  [subjectId: string]: number;
}

interface SubjectsFilterResultsProps {
  searchString: string;
  selectHandler: (subject: SubjectModelV2) => void;
  classroomIds: string[];
  searchResults: List<any>;
  selectedItems: Set<any>;
}

const SubjectsFilterResults = ({
  searchString,
  selectHandler,
  classroomIds,
  searchResults,
  selectedItems
}: SubjectsFilterResultsProps) => {
  const [classroomSubjects, setClassroomSubjects] = useState(List([]));
  const [isLoading, setIsLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [selectedItemCounts, setSelectedItemCounts] = useState({} as SubjectCounts);

  const {
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    getSelectedStandardsAndSets
  } = useContext(ContentDiscoveryContext);

  const {selectedStandardIds, selectedStandardSetIds} =
    getSelectedStandardsAndSets(standardCollection);

  const isSubjectSelected = useCallback(
    (item) => {
      return selectedItems.some((selected) => selected.getId() === item.getId());
    },
    [selectedItems]
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const query = getClassroomSubjectsQuery(
        searchString,
        classroomIds.join(','),
        undefined,
        questionDifficulties,
        questionTypes,
        selectedStandardIds,
        selectedStandardSetIds,
        hideAssessmentQuestions,
        hidePreviouslyAssigned
      );
      const results = await query.getResourcePromise();
      setClassroomSubjects(results);
      setIsLoading(false);
    };
    if (classroomIds.length > 0) {
      fetchData();
    }
  }, [searchString, setClassroomSubjects, classroomIds]);

  const filteredClassroomSubjects = useMemo(() => {
    return classroomSubjects.filter((result) => !isSubjectSelected(result));
  }, [classroomSubjects, isSubjectSelected]);

  const filteredSearchResults = useMemo(() => {
    return searchResults
      .filter((result) => !excludedIds.some((id) => id === result.getId()))
      .filter((result) => !isSubjectSelected(result))
      .filter(
        (result) => !classroomSubjects.some((subject: any) => subject.getId() === result.getId())
      );
  }, [searchResults, classroomSubjects, isSubjectSelected]);

  const filteredSelectedItems = useMemo(() => {
    return selectedItems
      .sort((itemA, itemB) => {
        if (itemA.getName() < itemB.getName()) {
          return -1;
        }
        if (itemA.getName() > itemB.getName()) {
          return 1;
        }
        return 0;
      })
      .filter((selectedItem) => {
        const name = selectedItem.getName().toLowerCase();
        const description = selectedItem.getDescription().toLowerCase();
        return searchString
          ? name.includes(searchString.toLowerCase()) ||
              description.includes(searchString.toLowerCase())
          : true;
      });
  }, [selectedItems, searchString]);

  useEffect(() => {
    const updateCounts = async () => {
      setCountsLoading(true);
      const subjectIds = filteredSelectedItems.map((item) => item.getId()).join();

      const results = await getSubjectsByIdQuery(
        subjectIds,
        questionDifficulties,
        questionTypes,
        selectedStandardIds,
        selectedStandardSetIds,
        hideAssessmentQuestions,
        hidePreviouslyAssigned
      ).getResourcePromise();

      const newCounts = {} as SubjectCounts;

      results.forEach((result) => {
        newCounts[result.getId()] = result.getMeta().getContentDiscoveryQuestionCount();
      });

      setSelectedItemCounts(newCounts);
      setCountsLoading(false);
    };

    if (filteredSelectedItems.size > 0) {
      updateCounts();
    }
  }, []);

  const renderRelevantSubjects = useCallback(() => {
    if (!filteredClassroomSubjects.size && !filteredSearchResults.size) {
      return <FindAndApply.Message>No matches</FindAndApply.Message>;
    }
    return (
      <>
        {filteredClassroomSubjects.map((classSubject) => (
          <SubjectListItem selectHandler={selectHandler} subject={classSubject} />
        ))}
        {searchString.length > 0 &&
          filteredSearchResults.map((result) => (
            <SubjectListItem selectHandler={selectHandler} subject={result} />
          ))}
      </>
    );
  }, [filteredSearchResults, filteredClassroomSubjects]);

  if (isLoading || countsLoading) return <LoadingIndicator />;

  return (
    <ListGroup
      data-test-id='standards-modal__subject-results'
      as='ul'
      border='none'
      className='u-pad-t_0 u-mar-t_0'
    >
      {filteredSelectedItems.map((selectedItem) => (
        <SubjectListItem
          selectHandler={selectHandler}
          subject={selectedItem}
          refreshedCount={selectedItemCounts[selectedItem.getId()]}
          isSelected
        />
      ))}
      <li className='u-display_flex u-gap_1 u-align-items_flex-start u-mar-b_1 u-pad-tb_1 u-pad-lr_2 u-border-b u-border-color_slate-200 u-color_slate-600'>
        Suggested Subjects
      </li>
      {renderRelevantSubjects()}
    </ListGroup>
  );
};

interface SubjectListItemProps {
  subject: any;
  selectHandler: (subject: SubjectModelV2) => void;
  isSelected?: boolean;
  refreshedCount?: number;
}

const SubjectListItem = ({
  subject,
  selectHandler,
  isSelected = false,
  refreshedCount
}: SubjectListItemProps) => {
  const id = subject.getId();
  const checkboxId = `subjects-filter-${id}`;
  const count = refreshedCount || subject.getMeta().getContentDiscoveryQuestionCount() || 0;

  return (
    <ListGroupItem
      key={subject.getId()}
      paddingLevel={1}
      border={false}
      as='li'
      className='u-display_flex u-gap_1 u-align-items_center'
      onClick={() => {
        const checkbox = document.getElementById(checkboxId);
        if (checkbox) {
          checkbox.click();
        }
      }}
    >
      <Checkbox
        id={checkboxId}
        aria-hidden={false}
        checked={isSelected}
        size='s'
        className='u-mar-l_1'
        onClick={(e) => {
          selectHandler(subject);
          e.stopPropagation();
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            selectHandler(subject);
          }
        }}
      />
      <Label
        htmlFor={subject.getId()}
        className='u-cursor_pointer subjects-filter__subject-list-item'
      >
        <Text size='m' emphasis={sessionStore.isSuper() && !count ? 'lowest' : undefined}>
          {subject.getName()}
        </Text>
        {
          /*
          TODO: Enable for all users once feature is complete
          See https://github.com/albert-io/nexus/issues/7059
        */
          sessionStore.isSuper() && (
            <Text className='u-mar-r_1' size='m' emphasis={!count ? 'lowest' : undefined}>
              {count}
            </Text>
          )
        }
      </Label>
    </ListGroupItem>
  );
};

export default SubjectsFilterResults;
